/**
 * /admin/tracks — renders inside <AdminLayout> via <Outlet />.
 *
 * Top panel: paste-URL preview → editable form → save (the create flow).
 * Bottom panel: every track, newest first, with row actions
 *               (edit, make-current, archive, hard delete).
 *
 * Admin-only — guarded by <RequireAdmin> in App.jsx.
 */

import { useEffect, useState } from 'react';

import {
  listAllTracksAdmin,
  previewTrack,
  createTrack,
  updateTrack,
  setCurrentTrack,
  deleteTrack,
} from '../../api/tracks.js';
import '../tracks.css';
import { usePageTitle } from '../../hooks/usePageTitle.js';


const STATUS_OPTIONS = [
  { value: 'draft',     label: 'draft' },
  { value: 'published', label: 'published' },
  { value: 'archived',  label: 'archived' },
];

const EMPTY_FORM = {
  youtube_id: '',
  title: '',
  artist: '',
  album: '',
  year: '',
  note: '',
  is_embeddable: true,
  thumbnail_url: '',
  status: 'published',
  set_current: true,
};


export default function TracksAdminPage() {
  usePageTitle('Admin · Tracks');

  // form state ─────────────────────────────────────
  const [urlInput, setUrlInput] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [previewNote, setPreviewNote] = useState(null);  // string | null
  const [previewIsWarning, setPreviewIsWarning] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formMode, setFormMode] = useState('idle');      // 'idle' | 'create' | 'edit'
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // list state ─────────────────────────────────────
  const [tracks, setTracks] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);

  async function refreshList() {
    setListLoading(true);
    setListError(null);
    try {
      setTracks(await listAllTracksAdmin(true));
    } catch (e) {
      setListError(e.message);
    } finally {
      setListLoading(false);
    }
  }

  useEffect(() => {
    refreshList();
  }, []);

  function resetForm() {
    setForm(EMPTY_FORM);
    setFormMode('idle');
    setEditingId(null);
    setUrlInput('');
    setPreviewNote(null);
    setPreviewIsWarning(false);
    setSaveError(null);
  }

  async function handlePreview() {
    const url = urlInput.trim();
    if (!url) return;
    setPreviewing(true);
    setPreviewNote(null);
    setPreviewIsWarning(false);
    try {
      const meta = await previewTrack(url);
      setForm({
        ...EMPTY_FORM,
        youtube_id: meta.youtube_id,
        title: meta.title || '',
        artist: meta.artist || '',
        thumbnail_url: meta.thumbnail_url || '',
        is_embeddable: meta.is_embeddable,
      });
      setFormMode('create');
      if (!meta.is_embeddable) {
        setPreviewNote(
          "heads up: this video has embedding disabled. you can still post it; the player will fall back to a thumbnail + 'watch on YouTube' link."
        );
        setPreviewIsWarning(true);
      }
    } catch (e) {
      setPreviewNote(e.message);
      setPreviewIsWarning(false);
    } finally {
      setPreviewing(false);
    }
  }

  function handleStartEdit(track) {
    setForm({
      youtube_id: track.youtube_id,
      title: track.title,
      artist: track.artist,
      album: track.album || '',
      year: track.year ?? '',
      note: track.note || '',
      is_embeddable: track.is_embeddable,
      thumbnail_url: track.thumbnail_url || '',
      status: track.status,
      set_current: track.is_current,
    });
    setEditingId(track.id);
    setFormMode('edit');
    setSaveError(null);
    setPreviewNote(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      const yearNum =
        form.year === '' || form.year === null
          ? null
          : Number.parseInt(form.year, 10);
      if (yearNum !== null && Number.isNaN(yearNum)) {
        throw new Error('year must be a number');
      }

      if (formMode === 'create') {
        await createTrack({
          youtube_id: form.youtube_id,
          title: form.title.trim(),
          artist: form.artist.trim(),
          album: form.album.trim() || null,
          year: yearNum,
          note: form.note,
          is_embeddable: form.is_embeddable,
          thumbnail_url: form.thumbnail_url || null,
          status: form.status,
          set_current: form.set_current,
        });
      } else if (formMode === 'edit') {
        await updateTrack(editingId, {
          title: form.title.trim(),
          artist: form.artist.trim(),
          album: form.album.trim() || null,
          year: yearNum,
          note: form.note,
          is_embeddable: form.is_embeddable,
          status: form.status,
        });
        // set_current flips through the dedicated endpoint (intentional:
        // avoids accidental promotion via plain PATCH).
        if (form.set_current && form.status === 'published') {
          await setCurrentTrack(editingId);
        }
      }
      resetForm();
      await refreshList();
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleMakeCurrent(track) {
    if (track.status !== 'published') {
      alert('only published tracks can be the current pick.');
      return;
    }
    try {
      await setCurrentTrack(track.id);
      await refreshList();
    } catch (e) {
      alert(`couldn't make current: ${e.message}`);
    }
  }

  async function handleArchive(track) {
    if (!window.confirm(`archive "${track.title}"? it can be restored.`)) return;
    try {
      await deleteTrack(track.id, false);
      await refreshList();
    } catch (e) {
      alert(`couldn't archive: ${e.message}`);
    }
  }

  async function handleHardDelete(track) {
    if (!window.confirm(
      `PERMANENTLY DELETE "${track.title}"? this wipes comments and reactions too. cannot be undone.`
    )) return;
    if (!window.confirm('really? last chance.')) return;
    try {
      await deleteTrack(track.id, true);
      await refreshList();
    } catch (e) {
      alert(`couldn't delete: ${e.message}`);
    }
  }

  return (
    <div className="tt-admin">
      <header className="tt-admin__header">
        <h1 className="tt-admin__title">Tasty Tracks</h1>
        <p className="tt-admin__subtitle">
          manage the current pick and the archive.
        </p>
      </header>

      {/* ─── create / edit form ─── */}
      <section className="tt-admin__panel">
        <h2 className="tt-admin__panel-title">
          {formMode === 'edit' ? `edit track #${editingId}` : 'add a new track'}
        </h2>

        {formMode !== 'edit' && (
          <div className="tt-admin__url-row">
            <input
              type="text"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="paste a YouTube URL…"
              className="tt-input tt-input--mono"
              onKeyDown={(e) => { if (e.key === 'Enter') handlePreview(); }}
            />
            <button
              type="button"
              className="tt-btn tt-btn--primary"
              onClick={handlePreview}
              disabled={previewing || !urlInput.trim()}
            >
              {previewing ? 'fetching…' : 'preview'}
            </button>
          </div>
        )}

        {previewNote && (
          <div className={previewIsWarning ? 'tt-warning' : 'tt-error'}>
            {previewNote}
          </div>
        )}

        {(formMode === 'create' || formMode === 'edit') && (
          <div className="tt-admin__form">
            <div className="tt-admin__form-grid">
              <Field label="YouTube ID">
                <input
                  type="text"
                  value={form.youtube_id}
                  readOnly
                  className="tt-input tt-input--mono tt-input--readonly"
                />
              </Field>

              <Field label="Title *">
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  className="tt-input"
                  maxLength={200}
                />
              </Field>

              <Field label="Artist *">
                <input
                  type="text"
                  value={form.artist}
                  onChange={(e) => setForm((f) => ({ ...f, artist: e.target.value }))}
                  className="tt-input"
                  maxLength={120}
                />
              </Field>

              <Field label="Album">
                <input
                  type="text"
                  value={form.album}
                  onChange={(e) => setForm((f) => ({ ...f, album: e.target.value }))}
                  className="tt-input"
                  maxLength={120}
                />
              </Field>

              <Field label="Year" narrow>
                <input
                  type="number"
                  value={form.year}
                  onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))}
                  className="tt-input"
                  min={1900}
                  max={2100}
                />
              </Field>

              <Field label="Status" narrow>
                <select
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                  className="tt-input"
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Note (Timmy's take)">
              <textarea
                value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                className="tt-input"
                rows={4}
                placeholder="why this track? what's tasty about it?"
              />
            </Field>

            <div className="tt-admin__toggles">
              <label className="tt-checkbox">
                <input
                  type="checkbox"
                  checked={form.is_embeddable}
                  onChange={(e) => setForm((f) => ({ ...f, is_embeddable: e.target.checked }))}
                />
                <span>embeddable on this site</span>
              </label>

              <label className="tt-checkbox">
                <input
                  type="checkbox"
                  checked={form.set_current}
                  onChange={(e) => setForm((f) => ({ ...f, set_current: e.target.checked }))}
                  disabled={form.status !== 'published'}
                />
                <span>
                  make this the <em>current</em> pick
                  {form.status !== 'published' && ' (publish first)'}
                </span>
              </label>
            </div>

            {saveError && <div className="tt-error">{saveError}</div>}

            <div className="tt-admin__form-actions">
              <button
                type="button"
                className="tt-btn tt-btn--primary"
                onClick={handleSave}
                disabled={saving || !form.title.trim() || !form.artist.trim()}
              >
                {saving
                  ? 'saving…'
                  : formMode === 'edit'
                    ? 'save changes'
                    : 'publish'}
              </button>
              <button
                type="button"
                className="tt-btn"
                onClick={resetForm}
                disabled={saving}
              >
                cancel
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ─── list ─── */}
      <section className="tt-admin__panel">
        <h2 className="tt-admin__panel-title">all tracks</h2>

        {listError && <div className="tt-error">{listError}</div>}

        {listLoading ? (
          <div className="tt-muted">loading…</div>
        ) : tracks.length === 0 ? (
          <div className="tt-muted">
            no tracks yet. paste a YouTube URL above to add the first one.
          </div>
        ) : (
          <table className="tt-admin__table">
            <thead>
              <tr>
                <th />
                <th>title</th>
                <th>artist</th>
                <th>status</th>
                <th>created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tracks.map((t) => (
                <tr key={t.id} className={t.is_current ? 'is-current' : ''}>
                  <td className="tt-admin__thumb-cell">
                    {t.thumbnail_url && <img src={t.thumbnail_url} alt="" loading="lazy" />}
                  </td>
                  <td>
                    <div className="tt-admin__title-cell">
                      {t.is_current && (
                        <span className="tt-admin__current-pill" title="currently featured">
                          NOW
                        </span>
                      )}
                      <span>{t.title}</span>
                    </div>
                    <div className="tt-admin__yt-id">{t.youtube_id}</div>
                  </td>
                  <td>{t.artist}</td>
                  <td>
                    <span className={`tt-status tt-status--${t.status}`}>
                      {t.status}
                    </span>
                  </td>
                  <td className="tt-admin__date">
                    {new Date(t.created_at).toLocaleDateString()}
                  </td>
                  <td className="tt-admin__actions-cell">
                    <button
                      type="button"
                      className="tt-btn tt-btn--ghost"
                      onClick={() => handleStartEdit(t)}
                    >
                      edit
                    </button>
                    {!t.is_current && t.status === 'published' && (
                      <button
                        type="button"
                        className="tt-btn tt-btn--ghost"
                        onClick={() => handleMakeCurrent(t)}
                      >
                        make current
                      </button>
                    )}
                    {t.status !== 'archived' && (
                      <button
                        type="button"
                        className="tt-btn tt-btn--ghost tt-btn--danger"
                        onClick={() => handleArchive(t)}
                      >
                        archive
                      </button>
                    )}
                    <button
                      type="button"
                      className="tt-btn tt-btn--ghost tt-btn--danger"
                      onClick={() => handleHardDelete(t)}
                      title="permanent deletion"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}


function Field({ label, narrow, children }) {
  return (
    <label className={`tt-field ${narrow ? 'tt-field--narrow' : ''}`}>
      <span className="tt-field__label">{label}</span>
      {children}
    </label>
  );
}
